import {inject, Injectable, signal} from "@angular/core";
import {AppNavigationModel} from "../_models/AppNavigationModel";
import {AbstractController} from "../../../common/controllers/AbstractController";
import {ModelKey} from "../../../common/models/ModelKey";
import {AppEventBus} from "../../../common/event-buses/app.event-bus";
import {AppEvent} from "../../../common/event-buses/AppEvent";
import {AppEventName} from "../../../common/event-buses/AppEventName";
import {NavigationEnd, NavigationStart, Router} from "@angular/router";
import {AppNavigationService} from "../_services/app-navigation.service";
import {CustomDatesService} from "../../../common/services/custom-dates.service";
import {AppMenuItem} from "../_models/AppMenuItem";
import {UserSessionRepository} from "../../../common/repositories/user-session.repository";
import {MenuItems} from "../_data/MenuItems";
import {Helper} from "../../../common/helpers/Helper";

export const MK_APP_NAVIGATION = new ModelKey<AppNavigationModel>('MK_APP_NAVIGATION');

@Injectable()
export class AppNavigationController extends AbstractController {

  private readonly router: Router = inject(Router);

  private readonly appNavigationService = inject(AppNavigationService);
  private readonly customDateService = inject(CustomDatesService);

  private readonly userSessionRepository = inject(UserSessionRepository);

  private readonly appEventBus = inject(AppEventBus);

  private intervalTracker: any;

  constructor() {
    super();

    this.addModel(MK_APP_NAVIGATION, {
      menuItems$: signal(this.appNavigationService.createMenuItems()),
      displayTimestamp$: signal(''),
      displayName$: signal('User'),
      impersonation$: signal(this.userSessionRepository.isImpersonation()),
    });

    this.sub(this.router.events.subscribe((event) => this.onRouterEvent(event)));

    this.sub(this.appEventBus.subscribe(AppEventName.NewUserSession, (event) => this.onNewUserSession(event)));
  }

  protected onNewUserSession(event: AppEvent): void {
    const model = this.getModel(MK_APP_NAVIGATION);

    model.menuItems$.set(this.appNavigationService.createMenuItems());
    model.impersonation$.set(this.userSessionRepository.isImpersonation());

    const userSession = this.userSessionRepository.getUserSession();

    if(userSession) {
      const displayName = `${userSession.firstName} ${userSession.lastName}`;
      model.displayName$.set(displayName);
    }

    if(this.userSessionRepository.isImpersonation()) {
      let menuItems = model.menuItems$();

      model.displayName$.set(`Impersonating ${model.displayName$()}`);

      menuItems = menuItems.filter((mi) => mi.type !== 'log-out' && mi.type !== 'leave-impersonation');
      menuItems.push(MenuItems.LeaveImpersonation);
      model.menuItems$.set(Helper.arrayCopy(menuItems));
    }
    else {
      let menuItems = model.menuItems$();

      menuItems = menuItems.filter((mi) => mi.type !== 'log-out' && mi.type !== 'leave-impersonation');
      menuItems.push(MenuItems.Logout);
      model.menuItems$.set(Helper.arrayCopy(menuItems));
    }
  }

  protected handleInit(eventData: any): void {
    const model = this.getModel(MK_APP_NAVIGATION);
    const userSession = this.userSessionRepository.getUserSession();

    if(userSession) {
      const displayName = `${userSession.firstName} ${userSession.lastName}`;
      model.displayName$.set(displayName);
    }

    this.intervalTracker = setInterval(() => {
      this.populateTimestamps();
    }, 1000);
  }

  protected handleMenuItemClicked(eventData: any): void {
    const menuItem: AppMenuItem = eventData['menuItem'];
    const event: any = eventData['event'];

    if(menuItem.type === 'path') {
      if(menuItem.path) {
        this.navigate(menuItem.path, event);
      }
    }
    else if(menuItem.type === 'parent-item') {
      menuItem.expanded = !menuItem.expanded;
    }
    else if(menuItem.type === 'user-profile') {
      const userId = this.getCurrentUserId();
      this.navigate(`/config/user/profile/view/${userId}`, event);
    }
    else if(menuItem.type === 'log-out') {
      this.appEventBus.dispatch(new AppEvent(AppEventName.LogoutRequest, {}));
    }
    else if(menuItem.type === 'leave-impersonation') {
      this.appEventBus.dispatch(new AppEvent(AppEventName.LeaveImpersonation, {}));
    }
  }

  protected onRouterEvent(event: any): void {
    if(event instanceof NavigationStart) {
      this.processNavigationStartEvent(event);
    }
    else if(event instanceof NavigationEnd) {
      this.processNavigationEndEvent(event);
    }
  }

  protected processNavigationStartEvent(event: NavigationStart): void {
    const model = this.getModel(MK_APP_NAVIGATION);
    const menuItems = model.menuItems$();

    this.appNavigationService.resetMenuItemStates(menuItems);
  }

  protected processNavigationEndEvent(event: NavigationEnd): void {
    const menuItem = this.findMenuItemByUrl(event.urlAfterRedirects);

    if(menuItem) {
      menuItem.active = true;

      let parentItem = menuItem.parent;

      while(parentItem) {
        parentItem.expanded = true;
        parentItem = parentItem.parent;
      }
    }
  }

  protected populateTimestamps(): void {
    const currentTime = new Date().getTime();
    const currentTimeDisplay = this.customDateService.convertMillisToUserFormat(currentTime);

    const model = this.getModel(MK_APP_NAVIGATION);
    model.displayTimestamp$.set(currentTimeDisplay);
  }

  protected findMenuItemByUrl(url: string): AppMenuItem|null|undefined {
    const model = this.getModel(MK_APP_NAVIGATION);
    const menuItems = model.menuItems$();

    const matchingItems = this.findMatchingMenuItemsByUrl(menuItems, url);

    if(matchingItems.length === 0) {
      return null;
    }
    else if(matchingItems.length === 1) {
      return matchingItems[0];
    }
    else {
      return matchingItems[matchingItems.length - 1];
    }
  }

  protected findMatchingMenuItemsByUrl(menuItems: Array<AppMenuItem>, url: string): Array<AppMenuItem> {
    const results: Array<AppMenuItem> = [];

    for(let i = 0; i < menuItems.length; i++) {
      const menuItem = menuItems[i];

      if(this.isMenuItemMatchingUrl(menuItem, url)) {
        results.push(menuItem);
      }

      if(menuItem.type === 'parent-item') {
        const subResults = this.findMatchingMenuItemsByUrl(menuItem.children, url);
        if(subResults.length > 0) {
          subResults.forEach((r) => results.push(r));
        }
      }
    }

    return results;
  }

  protected isMenuItemMatchingUrl(menuItem: AppMenuItem, url: string): boolean {
    if(menuItem.type === 'path' && menuItem.path) {
      if(url.startsWith(menuItem.path)) {
        return true;
      }
    }

    if(menuItem.type === 'path' && menuItem.matchingPaths.length > 0) {
      for(let i = 0; i < menuItem.matchingPaths.length; i++) {
        if(url.startsWith(menuItem.matchingPaths[i])) {
          return true;
        }
      }
    }

    if(menuItem.type === 'user-profile' && this.isLoggedIn() && (url === `/config/user/profile/view/${this.getCurrentUserId()}` || url === `/config/see-users/${this.getCurrentUserId()}/edit` || url === `/config/external-users/${this.getCurrentUserId()}/edit`)) {
      return true;
    }

    return false;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    if(this.intervalTracker) {
      clearInterval(this.intervalTracker);
    }
  }
}
